import { useEffect, useRef, useState } from 'react'
import type { TransactionKind } from '@greencandle/api-client'

/** What the reader picked. `txn` carries the type its form should start on. */
export type AddChoice =
  | { kind: 'txn'; type: TransactionKind }
  | { kind: 'asset' }
  | { kind: 'liability' }

/**
 * The terminal's one way to put something into the account.
 *
 * <p>It replaces a button labelled "+ Add asset" that added nothing: it was a link to the
 * transactions list, so it left the terminal, dropped the account the reader had selected, and
 * landed them on a page where they had to find one of two further buttons before any form
 * appeared. The label was wrong in a way that mattered too - "asset" covered both buying a
 * security, which is a transaction, and registering a flat or a private loan, which is an
 * instrument that has to exist before anything can be bought against it.
 *
 * <p>So the menu names the three things a person actually wants. It reports the choice and
 * nothing else: the form belongs below the toolbar, not inside it, so the page that owns the
 * layout opens it.
 */
export function AddMenu({ onChoose }: { onChoose: (choice: AddChoice) => void }) {
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)

  // A menu that only closes on its own items is a trap: clicking away and pressing Escape both
  // have to work, or a reader who opened it by accident cannot get rid of it.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  function choose(choice: AddChoice) {
    setOpen(false)
    onChoose(choice)
  }

  return (
    <div className="add-menu" ref={wrapper}>
      <button
        type="button"
        className="btn-primary"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        + Add ▾
      </button>

      {open && (
        <div className="add-menu-items" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => choose({ kind: 'txn', type: 'BUY' })}
          >
            Buy or sell a holding
            <span className="add-menu-note">a trade that opens or closes a position</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => choose({ kind: 'txn', type: 'DIVIDEND' })}
          >
            Cash movement
            <span className="add-menu-note">dividend, interest, fee, tax, transfer</span>
          </button>
          <hr />
          <button type="button" role="menuitem" onClick={() => choose({ kind: 'asset' })}>
            Property or private loan
            <span className="add-menu-note">
              registers it, so it can then be bought like anything else
            </span>
          </button>
          <button type="button" role="menuitem" onClick={() => choose({ kind: 'liability' })}>
            Mortgage or loan
            <span className="add-menu-note">
              something owed, subtracted from net worth
            </span>
          </button>
        </div>
      )}
    </div>
  )
}
